<div
     snAssistantSurface="benchmark"
     i18n-surfaceTitle="@@benchmark.surface"
     surfaceTitle="Benchmark" class="benchmark-edit-container">
  <div class="page-header">
    <div class="page-title">
      <p-button
        icon="pi pi-arrow-left"
        severity="secondary"
        [text]="true"
        [rounded]="true"
        pTooltip="Назад к списку"
        i18n-pTooltip="@@benchmarks.edit.back"
        (click)="cancel()"
        i18n-ariaLabel="@@benchmarks.edit.back"
        ariaLabel="Назад к списку">
      </p-button>
      @if (isEditMode()) {
        <h2 i18n="@@benchmarks.edit.titleEdit">Редактирование бенчмарка</h2>
      } @else {
        <h2 i18n="@@benchmarks.edit.titleNew">Новый бенчмарк</h2>
      }
      @if (loaded(); as detail) {
        <p-tag
          [value]="(detail.model_type_code || '').toUpperCase()"
          [rounded]="true"
          severity="info">
        </p-tag>
      }
    </div>

    @if (isEditMode()) {
      <div class="page-actions">
        <p-button
          icon="pi pi-trash"
          severity="danger"
          [outlined]="true"
          pTooltip="Удалить бенч"
          i18n-pTooltip="@@benchmarks.edit.delete.tooltip"
          [disabled]="isSaving() || isSavingAndRunning() || isDeleting()"
          (click)="onDeleteClick()"
          i18n-label="@@benchmarks.edit.delete"
          label="Удалить">
        </p-button>
      </div>
    }
  </div>

  @if (isLoading()) {
    <div class="loading-container">
      <p-progressSpinner styleClass="loading-spinner"></p-progressSpinner>
      <p i18n="@@benchmarks.edit.loading">Загрузка...</p>
    </div>
  } @else {
    <section class="form-section">
      <h3 class="section-title" i18n="@@benchmarks.edit.section.base">Base</h3>

      <div class="form-grid">
        <div class="field">
          <label i18n="@@benchmarks.edit.field.title">Title</label>
          <input pInputText
                 [ngModel]="title()"
                 (ngModelChange)="title.set($event)"
                 [disabled]="isSaving()"
                 i18n-placeholder="@@benchmarks.edit.field.titlePh"
                 placeholder="Название бенчмарка" />
        </div>

        <div class="field">
          <label i18n="@@benchmarks.edit.field.modelType">Тип модели</label>
          <p-select
            [ngModel]="modelTypeId()"
            (ngModelChange)="onTypeChange($event)"
            [options]="modelTypeOptions()"
            optionLabel="label"
            optionValue="value"
            [disabled]="isEditMode() || isSaving()"
            i18n-placeholder="@@benchmarks.edit.field.modelTypePh"
            placeholder="Выберите тип"
            styleClass="w-full">
          </p-select>
          @if (isEditMode()) {
            <small class="field-hint" i18n="@@benchmarks.edit.field.modelTypeLocked">
              Тип задаётся при создании и не меняется — бенчмарк привязан к своим эталонам.
            </small>
          }
        </div>

        <div class="field field-wide">
          <label i18n="@@benchmarks.edit.field.description">Description</label>
          <textarea pTextarea
                    rows="3"
                    [ngModel]="description()"
                    (ngModelChange)="description.set($event)"
                    [disabled]="isSaving()"
                    i18n-placeholder="@@benchmarks.edit.field.descriptionPh"
                    placeholder="Короткое описание — что и как бенчим (необязательно)">
          </textarea>
        </div>

        <div class="field">
          <label i18n="@@benchmarks.edit.field.locale">
            Язык бенча
            @if (isStt()) {
              <span class="required-marker">*</span>
            }
          </label>
          <p-select
            [ngModel]="languageLocaleId()"
            (ngModelChange)="languageLocaleId.set($event)"
            [options]="localeOptions()"
            optionLabel="label"
            optionValue="value"
            [filter]="true"
            [showClear]="!isStt()"
            [disabled]="isSaving()"
            i18n-placeholder="@@benchmarks.edit.field.localePh"
            placeholder="Выберите язык"
            styleClass="w-full">
          </p-select>
          <small class="field-hint" i18n="@@benchmarks.edit.field.localeHint">
            Язык всего бенчмарка: для STT — на нём эталоны и считается WER;
            для LLM — контекст для промпта (опционально).
          </small>
        </div>

        <div class="field">
          <label i18n="@@benchmarks.edit.field.iterations">Итераций на прогон</label>
          <p-inputNumber
            [ngModel]="iterations()"
            (ngModelChange)="iterations.set($event ?? 1)"
            [min]="1"
            [max]="1000"
            [showButtons]="true"
            [disabled]="isSaving()"
            styleClass="w-full">
          </p-inputNumber>
          <small class="field-hint" i18n="@@benchmarks.edit.field.iterationsHint">
            Сколько раз прогонять каждый эталон в одном ране. Больше — стабильнее p50/p95.
          </small>
        </div>

        <div class="field switch-field">
          <label class="switch-inline">
            <p-toggleSwitch
              [ngModel]="autoBenchmarkEnabled()"
              (ngModelChange)="autoBenchmarkEnabled.set($event)"
              [disabled]="isSaving()">
            </p-toggleSwitch>
            <span i18n="@@benchmarks.edit.field.auto">Авто-бенч раз в час</span>
          </label>
          <small class="field-hint" i18n="@@benchmarks.edit.field.autoHint">
            Каждый час Hangfire enqueue'ит прогон этого бенчмарка на все подключённые версии.
          </small>
        </div>
      </div>
    </section>

    @if (isLlm()) {
      <section class="form-section">
        <h3 class="section-title" i18n="@@benchmarks.edit.section.llm">LLM параметры</h3>
        <small class="field-hint" i18n="@@benchmarks.edit.llmHint">
          Temperature / top_k / top_p / max output tokens задаются per-версия в секции «Модели для бенча» —
          там каждая привязанная версия имеет свои переопределения. Здесь общие для бенча системный промпт и стартовый диалог.
        </small>

        <!-- Agent seed picker: копирует system_prompt + первое сообщение
             primary-локали в форму. Заменяет ручной ввод в один клик. -->
        <div class="agent-seed-row">
          <label class="agent-seed-label" i18n="@@benchmarks.edit.seed.label">Заполнить из агента</label>
          <p-select
            [ngModel]="seedAgentId()"
            (ngModelChange)="seedAgentId.set($event)"
            [options]="agentOptions()"
            optionLabel="label"
            optionValue="value"
            [filter]="true"
            [showClear]="true"
            [disabled]="isSaving() || isSeedingFromAgent() || isLoadingAgents()"
            i18n-placeholder="@@benchmarks.edit.seed.placeholder"
            placeholder="Выберите агента..."
            styleClass="agent-seed-select">
          </p-select>
          <p-button
            icon="pi pi-bolt"
            severity="secondary"
            [outlined]="true"
            [disabled]="!seedAgentId() || isSaving() || isSeedingFromAgent()"
            [loading]="isSeedingFromAgent()"
            (click)="fillFromAgent(seedAgentId())"
            i18n-label="@@benchmarks.edit.seed.fill"
            label="Заполнить">
          </p-button>
        </div>

        <div class="form-grid">
          <div class="field field-wide">
            <label i18n="@@benchmarks.edit.field.systemPrompt">Системный промпт</label>
            <textarea pTextarea
                      rows="5"
                      [ngModel]="systemPrompt()"
                      (ngModelChange)="systemPrompt.set($event)"
                      [disabled]="isSaving()"
                      i18n-placeholder="@@benchmarks.edit.field.systemPromptPh"
                      placeholder="Роль и инструкции модели (System message)">
            </textarea>
          </div>

          <div class="field field-wide">
            <div class="field-label-row">
              <label i18n="@@benchmarks.edit.field.dialog">Стартовый диалог</label>
              @if (dialogMessages().length > 0) {
                <p-button
                  icon="pi pi-times"
                  severity="secondary"
                  [text]="true"
                  size="small"
                  (click)="clearDialog()"
                  [disabled]="isSaving()"
                  i18n-label="@@benchmarks.edit.clearDialog"
                  label="Очистить">
                </p-button>
              }
            </div>
            <small class="field-hint" i18n="@@benchmarks.edit.field.dialogHint">
              Реплики User / Assistant, которые подаются модели вместе с системным промптом.
            </small>
            <app-chat-history-view
              [messages]="dialogMessages()"
              [editable]="true"
              [showMetrics]="false"
              [showTimestamps]="false"
              (messagesChange)="onDialogChange($event)">
            </app-chat-history-view>
          </div>
        </div>
      </section>
    }

    @if (isStt() && isEditMode() && benchmarkId(); as bid) {
      <section class="form-section">
        <h3 class="section-title" i18n="@@benchmarks.edit.section.sttSamples">STT эталоны</h3>
        <small class="field-hint" i18n="@@benchmarks.edit.sttHint">
          Загрузите аудио + эталонный текст. Бэк сконвертит файл в WAV 16k mono
          и посчитает длительность автоматически. Кейс (акустика) нужен для WER-by-case
          агрегатов в прогонах.
        </small>

        <app-benchmark-stt-samples
          [benchmarkId]="bid"
          [samples]="sttSamples()"
          (added)="onSttSampleAdded($event)"
          (removed)="onSttSampleRemove($event)">
        </app-benchmark-stt-samples>
      </section>
    }

    @if (isEditMode()) {
      <section class="form-section history-cta">
        <div class="history-cta-body">
          <div>
            <h3 class="section-title" i18n="@@benchmarks.edit.section.history">История прогонов</h3>
            <small class="field-hint" i18n="@@benchmarks.edit.historyHint">
              Все прогоны этого бенчмарка (manual + auto), per-sample метрики — на отдельной странице.
            </small>
          </div>
          <p-button
            icon="pi pi-history"
            severity="secondary"
            [outlined]="true"
            [routerLink]="['/admin/benchmarks', benchmarkId(), 'runs']"
            i18n-label="@@benchmarks.edit.openHistory"
            label="Открыть историю прогонов">
          </p-button>
        </div>
      </section>
    }

    @if (modelTypeCode()) {
      <section class="form-section">
        <h3 class="section-title" i18n="@@benchmarks.edit.section.linked">Модели для бенча</h3>
        <small class="field-hint" i18n="@@benchmarks.edit.linkedHint">
          Выберите версии, на которых этот бенчмарк будет прогоняться. Один и тот же
          набор эталонов запускается на всех отмеченных версиях; переключение — в мультиселекте.
        </small>

        <!-- Набор моделей для замера: это лабораторный стенд, агент им не
               пользуется. -->
          <app-model-selector-button
            snAssistantSkip
          [modelTypes]="[modelTypeCode()]"
          [modelsHierarchy]="modelsHierarchy()"
          [multiSelect]="true"
          [selectedVersionIds]="linkedVersionIds()"
          [versionSamplingParams]="linkedSamplingParams()"
          [includeAll]="includeAll()"
          (includeAllChange)="onIncludeAllChange($event)"
          [showVersions]="true"
          [showPropertiesEdit]="true"
          [allowSelectFromTester]="true"
          i18n-label="@@benchmarks.edit.selectVersions"
          label="Выберите версии моделей"
          (modelsSelected)="onLinkedVersionsChange($event)"
          (testerSelected)="onTesterSelected($event)">
        </app-model-selector-button>
      </section>
    }

    <div class="form-footer">
      <p-button
        severity="secondary"
        [text]="true"
        (click)="cancel()"
        i18n-label="@@common.cancel"
        label="Cancel"
        [disabled]="isSaving() || isSavingAndRunning()">
      </p-button>
      <p-button
        icon="pi pi-save"
        severity="primary"
        (click)="save()"
        i18n-label="@@common.save"
        label="Save"
        [loading]="isSaving() && !isSavingAndRunning()"
        [disabled]="!canSave() || isSavingAndRunning()">
      </p-button>
      <p-button
        icon="pi pi-bolt"
        severity="warn"
        (click)="saveAndRun()"
        i18n-label="@@benchmarks.edit.saveAndRun"
        label="Сохранить и запустить"
        [loading]="isSavingAndRunning()"
        [disabled]="!canSave() || isSaving()">
      </p-button>
    </div>
  }

  <app-delete-confirmation
    [visible]="deleteVisible()"
    [deleting]="isDeleting()"
    [title]="'Удалить «' + (loaded()?.title || 'бенч') + '»?'"
    [subtitle]="'Удаление бенча снимет все связи с версиями моделей и историю прогонов. Действие необратимо.'"
    (confirm)="onDeleteConfirm()"
    (cancel)="onDeleteCancel()"
    (visibleChange)="deleteVisible.set($event)">
  </app-delete-confirmation>

</div>
